/**
 * Copyright (c) 2025 Lauft App Inc. All rights reserved.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import appIcon from '../../assets/icon portal.png';
import portalView from '../../assets/portalview.png';
import wreath from '../../assets/wreath.png';

const AgentSignup = ({ onSignupSuccess }) => {
  const [availablePools, setAvailablePools] = useState([]);
  const [isLoadingPools, setIsLoadingPools] = useState(true);
  const [gclid, setGclid] = useState(null); // Google Ads click ID for conversion tracking

  const BROKERAGE_SUGGESTIONS = [
    'RE/MAX',
    'Keller Williams',
    'Coldwell Banker',
    'eXp Realty LLC',
    'REAL Broker',
    'COMPASS',
    'Douglas Elliman',
  ];

  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState('forward');
  const totalSteps = 3;

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', agency: '', password: '', confirmPassword: ''
  });
  
  const [selectedPools, setSelectedPools] = useState([]);
  const [poolSearch, setPoolSearch] = useState('');
  const [showPoolDropdown, setShowPoolDropdown] = useState(false);
  const [showBrokerageDropdown, setShowBrokerageDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const dropdownRef = useRef(null);
  const brokerageRef = useRef(null);
  const inputRefs = { name: useRef(null), agency: useRef(null), password: useRef(null) };

  // Fetch available pools on mount
  useEffect(() => {
    const fetchPools = async () => {
      try {
        console.log('Fetching pools from API...');
        const response = await fetch('https://back-end-x7tx.onrender.com/api/agent-pools/public');
        console.log('Response status:', response.status);
        const data = await response.json();
        console.log('Pools data:', data);
        if (data.success) {
          setAvailablePools(data.data);
          console.log('Loaded pools:', data.data.length);
        } else {
          console.error('API returned error:', data);
        }
      } catch (err) {
        console.error('Error fetching pools:', err);
      } finally {
        setIsLoadingPools(false);
      }
    };
    fetchPools();
  }, []);

  // Capture GCLID from URL for Google Ads conversion tracking
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const gclidParam = urlParams.get('gclid');
    if (gclidParam) {
      console.log('📊 GCLID captured:', gclidParam);
      setGclid(gclidParam);
      // Also store in sessionStorage in case they navigate away and come back
      sessionStorage.setItem('gclid', gclidParam);
    } else {
      // Check sessionStorage for previously captured GCLID
      const storedGclid = sessionStorage.getItem('gclid');
      if (storedGclid) {
        console.log('📊 GCLID restored from session:', storedGclid);
        setGclid(storedGclid);
      }
    }
  }, []);

  useEffect(() => {
    const focusMap = { 1: 'name', 2: 'agency', 3: 'password' };
    const ref = inputRefs[focusMap[currentStep]];
    if (ref?.current) setTimeout(() => ref.current.focus(), 300);
  }, [currentStep]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowPoolDropdown(false);
      if (brokerageRef.current && !brokerageRef.current.contains(e.target)) setShowBrokerageDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey && !showPoolDropdown && !showBrokerageDropdown) {
        e.preventDefault();
        handleNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep, formData, selectedPools, showPoolDropdown, showBrokerageDropdown]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const formatPhone = (value) => {
    const n = value.replace(/\D/g, '');
    if (n.length <= 3) return n;
    if (n.length <= 6) return `(${n.slice(0, 3)}) ${n.slice(3)}`;
    return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    setFormData(prev => ({ ...prev, phone: formatPhone(e.target.value) }));
    setError('');
  };

  const handleCitySelect = (pool) => {
    if (!selectedPools.find(p => p._id === pool._id)) {
      setSelectedPools(prev => [...prev, pool]);
    }
    setPoolSearch('');
    setShowPoolDropdown(false);
  };

  const handleRemoveCity = (poolId) => {
    setSelectedPools(prev => prev.filter(p => p._id !== poolId));
  };

  const filteredPools = availablePools.filter(pool => {
    const displayName = pool.serviceArea?.city 
      ? `${pool.serviceArea.city}${pool.serviceArea.state ? `, ${pool.serviceArea.state}` : ''}`
      : pool.name;
    return displayName.toLowerCase().includes(poolSearch.toLowerCase()) &&
      !selectedPools.find(p => p._id === pool._id);
  });

  const filteredBrokerages = BROKERAGE_SUGGESTIONS.filter(b => 
    b.toLowerCase().includes(formData.agency.toLowerCase()) &&
    b.toLowerCase() !== formData.agency.toLowerCase()
  );

  const validateStep = (step) => {
    if (step === 1) {
      if (!formData.name.trim()) { setError('Please enter your name'); return false; }
      if (!formData.email.trim() || !/^\S+@\S+\.\S+$/.test(formData.email)) { setError('Please enter a valid email'); return false; }
      if (formData.phone.replace(/\D/g, '').length < 10) { setError('Please enter a valid phone number'); return false; }
    } else if (step === 2) {
      if (!formData.agency.trim()) { setError('Please enter your brokerage'); return false; }
      if (selectedPools.length === 0) { setError('Please select at least one territory'); return false; }
    } else if (step === 3) {
      if (formData.password.length < 6) { setError('Password must be at least 6 characters'); return false; }
      if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return false; }
    }
    return true;
  };

  const handleNext = () => {
    setError('');
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setDirection('forward');
        setCurrentStep(prev => prev + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    setError('');
    setDirection('back');
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('https://back-end-x7tx.onrender.com/api/auth/register-agent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email.toLowerCase(),
          password: formData.password,
          phone: formData.phone,
          agency: formData.agency,
          poolIds: selectedPools.map(p => p._id),
          gclid: gclid // Google Ads click ID for conversion tracking
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Registration failed');

      if (data.token && data.user) {
        // Store auth data - must match AuthService keys
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('user_data', JSON.stringify(data.user));
        
        // Clear the GCLID from sessionStorage after successful signup
        sessionStorage.removeItem('gclid');

// 🔥 Fire Meta Pixel conversion event
if (typeof window.fbq !== 'undefined') {
    console.log('🎯 Firing Meta CompleteRegistration event');
    window.fbq('track', 'CompleteRegistration', {
      content_name: 'Agent Trial Signup',
      currency: 'USD',
      value: 0
    });
  } else {
    console.log('⚠️ Meta Pixel not loaded');
  }
  
        
        // Force a small delay to ensure localStorage is written
        await new Promise(resolve => setTimeout(resolve, 100));
        
        if (onSignupSuccess) {
          onSignupSuccess(data.user);
        } else {
          // Use replace to prevent back button returning to signup
          window.location.replace('/agent');
        }
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="signup-page">
      {/* Left Side - Form */}
      <div className="form-side">
        <div className="form-container">
          <img src={appIcon} alt="Lauft" className="app-icon" />
          <h1 className="form-title">Start your free 14-day trial</h1>
          <p className="form-subtitle">See local user activity and explore the agent portal before activating a full account. No credit card required.</p>

          <div className={`step-content ${direction}`} key={currentStep}>
            {currentStep === 1 && (
              <>
                <input ref={inputRefs.name} type="text" name="name" value={formData.name} onChange={handleChange} className="form-input" placeholder="Full Name" />
                <input type="email" name="email" value={formData.email} onChange={handleChange} className="form-input" placeholder="Business Email" />
                <input type="tel" name="phone" value={formData.phone} onChange={handlePhoneChange} className="form-input" placeholder="Business Phone" />
              </>
            )}

            {currentStep === 2 && (
              <>
                <div className="brokerage-field" ref={brokerageRef}>
                  <input 
                    ref={inputRefs.agency} 
                    type="text" 
                    name="agency" 
                    value={formData.agency} 
                    onChange={(e) => { handleChange(e); setShowBrokerageDropdown(true); }}
                    onFocus={() => setShowBrokerageDropdown(true)}
                    className="form-input" 
                    placeholder="Employing Brokerage" 
                  />
                  {showBrokerageDropdown && filteredBrokerages.length > 0 && formData.agency && (
                    <div className="brokerage-dropdown">
                      {filteredBrokerages.map(b => (
                        <div 
                          key={b} 
                          className="brokerage-option" 
                          onClick={() => { setFormData(prev => ({ ...prev, agency: b })); setShowBrokerageDropdown(false); }}
                        >
                          {b}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="city-field" ref={dropdownRef}>
                  {selectedPools.length > 0 && (
                    <div className="selected-cities">
                      {selectedPools.map(pool => (
                        <span key={pool._id} className="city-tag">
                          {pool.serviceArea?.city || pool.name}
                          <button type="button" onClick={() => handleRemoveCity(pool._id)} className="city-remove">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <input
                    type="text"
                    value={poolSearch}
                    onChange={(e) => { setPoolSearch(e.target.value); setShowPoolDropdown(true); }}
                    onFocus={() => setShowPoolDropdown(true)}
                    className="form-input"
                    placeholder={isLoadingPools ? "Loading territories..." : "Territories you work in"}
                    disabled={isLoadingPools}
                  />
                  {showPoolDropdown && filteredPools.length > 0 && (
                    <div className="city-dropdown">
                      {filteredPools.map(pool => (
                        <div key={pool._id} className="city-option" onClick={() => handleCitySelect(pool)}>
                          {pool.serviceArea?.city 
                            ? `${pool.serviceArea.city}${pool.serviceArea.state ? `, ${pool.serviceArea.state}` : ''}`
                            : pool.name
                          }
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {currentStep === 3 && (
              <>
                <div className="email-display">{formData.email}</div>
                <div className="password-field">
                  <input ref={inputRefs.password} type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleChange} className="form-input" placeholder="Password" />
                  <button type="button" className="toggle-pw" onClick={() => setShowPassword(!showPassword)}>{showPassword ? 'Hide' : 'Show'}</button>
                </div>
                <div className="password-field">
                  <input type={showConfirmPassword ? "text" : "password"} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} className="form-input" placeholder="Confirm Password" />
                  <button type="button" className="toggle-pw" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>{showConfirmPassword ? 'Hide' : 'Show'}</button>
                </div>
              </>
            )}

            {error && <div className="error-msg">{error}</div>}

            <div className="nav-row">
              {currentStep > 1 && <button type="button" className="back-btn" onClick={handleBack}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg></button>}
              <button type="button" className="main-btn" onClick={handleNext} disabled={isLoading}>
                {isLoading ? 'CREATING...' : currentStep === totalSteps ? 'START TRIAL' : 'CONTINUE'}
              </button>
            </div>

            <div className="step-dots">
              {[1, 2, 3].map(s => <span key={s} className={`dot ${currentStep === s ? 'active' : ''}`} />)}
            </div>
          </div>

          <a href="https://calendly.com/team-lauft/demo" target="_blank" rel="noopener noreferrer" className="demo-btn">More Questions? Book Demo</a>
          <Link to="/" className="login-link">Already have an account? Login</Link>
          <p className="disclaimer">After the trial period you may activate a full account membership for $49 per month, month-to-month, cancel anytime.</p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="image-side">
        <h1 className="hero-text">Your next client is already swiping.</h1>
        <div className="phone-glow"></div>
        <img src={portalView} alt="Lauft Portal" className="portal-img" />
        
        <div className="review-badge">
          <img src={wreath} alt="5 Stars" className="wreath-img" />
          <div className="review-text">
            <span className="stars">★★★★★</span>
            <span className="review-label">Claim local users</span>
          </div>
        </div>
      </div>

      <style jsx>{`
        .signup-page {
          display: flex;
          min-height: 100vh;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .form-side {
          width: 38%;
          min-width: 400px;
          background: #1a1a1a;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px;
          position: relative;
          z-index: 10;
          box-shadow: 10px 0 40px rgba(0,0,0,0.5);
        }

        .form-container {
          width: 100%;
          max-width: 340px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .app-icon {
          height: 60px;
          width: 60px;
          border-radius: 12px;
          margin-bottom: 20px;
          align-self: center;
        }

        .form-title {
          color: #ffffff;
          font-size: 24px;
          font-weight: 600;
          margin: 0 0 12px 0;
          text-align: left;
          width: 100%;
        }

        .form-subtitle {
          color: rgba(255,255,255,0.6);
          font-size: 14px;
          line-height: 1.5;
          margin: 0 0 28px 0;
          text-align: left;
          width: 100%;
        }

        .step-content {
          width: 100%;
        }

        .step-content.forward { animation: slideRight 0.3s ease; }
        .step-content.back { animation: slideLeft 0.3s ease; }

        @keyframes slideRight {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes slideLeft {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }

        .form-input {
          width: 100%;
          padding: 14px 16px;
          background: #fff;
          border: none;
          border-radius: 4px;
          font-size: 15px;
          color: #333;
          margin-bottom: 12px;
          box-sizing: border-box;
        }

        .form-input:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(245, 166, 35, 0.5);
        }

        .form-input::placeholder { color: #888; }

        .email-display {
          width: 100%;
          padding: 14px 16px;
          background: rgba(255,255,255,0.1);
          border-radius: 4px;
          color: #fff;
          font-size: 15px;
          margin-bottom: 12px;
          text-align: center;
          box-sizing: border-box;
        }

        .password-field {
          position: relative;
          width: 100%;
        }

        .toggle-pw {
          position: absolute;
          right: 12px;
          top: 13px;
          background: none;
          border: none;
          color: #888;
          font-size: 13px;
          cursor: pointer;
        }

        .toggle-pw:hover { color: #333; }

        .city-field {
          width: 100%;
          position: relative;
        }

        .brokerage-field {
          width: 100%;
          position: relative;
          margin-bottom: 12px;
        }

        .brokerage-field .form-input {
          margin-bottom: 0;
        }

        .brokerage-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: #fff;
          border-radius: 4px;
          max-height: 200px;
          overflow-y: auto;
          z-index: 100;
          margin-top: 4px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .brokerage-option {
          padding: 12px 16px;
          cursor: pointer;
          color: #333;
          font-size: 14px;
        }

        .brokerage-option:hover { background: #f5f5f5; }

        .selected-cities {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 12px;
        }

        .city-tag {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #f5a623;
          color: #1a1a1a;
          padding: 6px 10px;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 500;
        }

        .city-remove {
          background: none;
          border: none;
          color: #1a1a1a;
          cursor: pointer;
          font-size: 16px;
          padding: 0;
          margin-left: 4px;
        }

        .city-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: #fff;
          border-radius: 4px;
          max-height: 160px;
          overflow-y: auto;
          z-index: 100;
          margin-top: 4px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .city-option {
          padding: 12px 16px;
          cursor: pointer;
          color: #333;
          font-size: 14px;
        }

        .city-option:hover { background: #f5f5f5; }

        .error-msg {
          color: #ff6b6b;
          font-size: 13px;
          margin-bottom: 12px;
          text-align: center;
        }

        .nav-row {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          margin-top: 4px;
        }

        .back-btn {
          background: none;
          border: none;
          color: rgba(255,255,255,0.6);
          cursor: pointer;
          padding: 14px 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .back-btn:hover { color: #fff; }

        .main-btn {
          flex: 1;
          padding: 14px 24px;
          background: #fff;
          border: none;
          border-radius: 4px;
          color: #1a1a1a;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          letter-spacing: 0.5px;
        }

        .main-btn:hover:not(:disabled) { background: #f0f0f0; }
        .main-btn:disabled { opacity: 0.7; cursor: not-allowed; }

        .step-dots {
          display: flex;
          justify-content: center;
          gap: 8px;
          margin-top: 20px;
          width: 100%;
        }

        .dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(255,255,255,0.2);
        }

        .dot.active { background: #fff; }

        .demo-btn {
          display: inline-block;
          padding: 10px 24px;
          background: transparent;
          border: 1px solid rgba(255,255,255,0.4);
          border-radius: 4px;
          color: rgba(255,255,255,0.8);
          font-size: 13px;
          text-decoration: none;
          margin-top: 28px;
          align-self: center;
          transition: all 0.2s;
        }

        .demo-btn:hover {
          border-color: #fff;
          color: #fff;
        }

        .login-link {
          color: rgba(255,255,255,0.6);
          font-size: 14px;
          text-decoration: none;
          margin-top: 16px;
          align-self: center;
        }

        .login-link:hover { color: #fff; text-decoration: underline; }

        .disclaimer {
          color: rgba(255,255,255,0.4);
          font-size: 12px;
          text-align: center;
          margin-top: 28px;
          line-height: 1.6;
          max-width: 280px;
          align-self: center;
        }

        .image-side {
          flex: 1;
          background: linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.6)),
                      url('https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=80');
          background-size: cover;
          background-position: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px;
          overflow: hidden;
          position: relative;
        }

        .hero-text {
          color: #fff;
          font-size: 56px;
          font-weight: 700;
          line-height: 1.05;
          margin: 0 0 32px 0;
          text-align: center;
          animation: fadeSlideIn 1s ease-out;
        }

        @keyframes fadeSlideIn {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        .phone-glow {
          position: absolute;
          width: 400px;
          height: 300px;
          background: radial-gradient(ellipse, rgba(245,166,35,0.2) 0%, transparent 70%);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -20%);
          animation: pulse 5s ease-in-out infinite;
          pointer-events: none;
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.4; transform: translate(-50%, -30%) scale(1); }
          50% { opacity: 0.6; transform: translate(-50%, -30%) scale(1.05); }
        }

        .portal-img {
          max-width: 95%;
          max-height: 450px;
          filter: drop-shadow(0 20px 60px rgba(0,0,0,0.5));
          border-radius: 12px;
          position: relative;
          z-index: 2;
        }

        .review-badge {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 32px;
          position: relative;
          z-index: 2;
        }

        .wreath-img {
          height: 50px;
          width: auto;
        }

        .review-text {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .stars {
          color: #f5a623;
          font-size: 16px;
          letter-spacing: 2px;
        }

        .review-label {
          color: rgba(255,255,255,0.8);
          font-size: 13px;
          font-weight: 500;
        }

        @media (max-width: 800px) {
          .signup-page { flex-direction: column; }
          .form-side { width: 100%; min-width: unset; min-height: 100vh; box-shadow: none; }
          .image-side { display: none; }
        }
      `}</style>
    </div>
  );
};

export default AgentSignup;